import { useCallback, useEffect, useState } from 'react';
import {
  fetchWorkerHealth,
  isLocalDevApi,
  isWorkerFirebaseReadyWithRetry,
  REQUIRED_API_VERSION
} from '@/lib/workerApi';

const POLL_MS = 8000;

/**
 * Tracks API worker health. Retries on mount (avoids a false "not configured" flash)
 * and polls while local dev API is down so the banner hides when the worker starts.
 */
export function useWorkerHealth() {
  const localDev = isLocalDevApi();
  const [ready, setReady] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);

  const check = useCallback(async (attempts = 3) => {
    const ok = await isWorkerFirebaseReadyWithRetry(attempts);
    setReady(ok);
    setChecking(false);
    return ok;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const ok = await isWorkerFirebaseReadyWithRetry(5);
      if (!cancelled) {
        setReady(ok);
        setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (ready !== false) return;
    const id = window.setInterval(() => {
      void fetchWorkerHealth().then((h) => {
        if (h.ok && h.firebaseConfigured && h.apiVersion >= REQUIRED_API_VERSION) setReady(true);
      });
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [ready]);

  /** Banner after health checks when the API worker is down (dev or production). */
  const showUnavailableBanner = !checking && ready === false;
  /** @deprecated Use showUnavailableBanner — kept for existing call sites. */
  const showDevBanner = localDev && showUnavailableBanner;

  return { ready, checking, showUnavailableBanner, showDevBanner, recheck: () => check(3) };
}
